import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getSales, downloadSales } from '../../actions/sales';
import { saveAs } from 'file-saver';
import { 
    ArrowLeft, 
    TrendingUp, 
    Download, 
    Calendar, 
    Filter, 
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    FileText,
    DollarSign,
    Shield,
    Receipt,
    RefreshCw,
    SortAsc,
    SortDesc
} from 'lucide-react';

const Sales = ({ sales, downloadBlob, getSales, downloadSales, pages, activePage, loading }) => {
    const initialState = {
        startDate: '',
        endDate: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [pageCount, setPage] = useState(0);
    const [currentPage, setActivePage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [download, setDownload] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        if(downloadBlob && download){
            const blob = new Blob([downloadBlob], { type: 'application/pdf' });
            saveAs(blob, "sales.pdf");
            setDownload(false);
            setDownloadingId(null);
        }
        if(!sales){
            let query = queryString;
            setQueryString(query);
            getSales(query);
        } else {
            let salesData = [];
            sales.forEach(sale => {
                salesData.push({
                    month: new Date(sale.date).toLocaleString('default', { month: 'long' }),
                    date: sale.date,
                    sales: sale.sales,
                    refundableDeposit: sale.refundableDeposit,
                    securityDeposit: sale.securityDeposit,
                    gst: sale.gst,
                    download: sale._id
                });
            });
            setQueryString(queryString.replace("page=" + currentPage, "page=" + activePage));
            setData(salesData);
            setPage(pages);
            setActivePage(activePage);
        }
    }, [sales, downloadBlob]);

    const { startDate, endDate, latest } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = e => {
        e.preventDefault();
        const startDateMs = new Date(formData.startDate).getTime();
        const endDateMs = new Date(formData.endDate).getTime();
        if(endDateMs < startDateMs){
            alert("End date cannot be earlier than start date");
            return;
        }
        let queryString = "page=1&latest=" + formData.latest;
        if(formData.startDate && formData.endDate){
            queryString += "&startDate=" + formData.startDate + "&endDate=" + formData.endDate;
        }
        setQueryString(queryString);
        getSales(queryString);
    };

    const downloadSalesPDF = async (e, id) => {
        setDownloadingId(id);
        downloadSales("id=" + id);
        setDownload(true);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? 
                <SortAsc size={14} className="ms-1 text-primary" /> : 
                <SortDesc size={14} className="ms-1 text-primary" />;
        }
        return null;
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return data;
        
        return [...data].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    const onPageChange = (pageNo) => {
        let query = "";
        if(queryString){
            query = queryString.replace("page=" + activePage, "page=" + pageNo);
        } else {
            query = "page=" + pageNo + "&latest=true";
        }
        getSales(query);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <TrendingUp size={32} className="me-3 text-primary" />
                                    Sales Reports
                                </h1>
                                <p className="text-muted mb-0">
                                    Track and download sales data with detailed financial breakdowns
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {data.length} Record{data.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search/Filter Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-light">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Filter size={20} className="me-2 text-primary" />
                                    Filter Sales Data
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={onSubmit}>
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-success" />
                                                Start Date
                                            </label>
                                            <input
                                                name="startDate"
                                                value={startDate}
                                                onChange={onChange}
                                                type="date"
                                                className="form-control"
                                                placeholder="Start Date"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-warning" />
                                                End Date
                                            </label>
                                            <input
                                                name="endDate"
                                                value={endDate}
                                                onChange={onChange}
                                                type="date"
                                                className="form-control"
                                                placeholder="End Date"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold">
                                                <SortAsc size={16} className="me-2 text-info" />
                                                Sort Order
                                            </label>
                                            <select
                                                name="latest"
                                                value={latest}
                                                className="form-select"
                                                onChange={onChange}
                                            >
                                                <option value="false">📅 Oldest First</option>
                                                <option value="true">🆕 Newest First</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <button type="submit" className="btn btn-primary w-100 d-flex align-items-center justify-content-center">
                                                <Search size={16} className="me-2" />
                                                Apply Filters
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sales Data Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Receipt size={20} className="me-2 text-primary" />
                                    Sales Data
                                    {data.length > 0 && (
                                        <span className="badge bg-info ms-2">{data.length} records</span>
                                    )}
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                {data.length > 0 ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('month')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={16} className="me-2" />
                                                                Month
                                                                {getSortIcon('month')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('date')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <Calendar size={16} className="me-2" />
                                                                Date
                                                                {getSortIcon('date')}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="border-0 fw-bold cursor-pointer user-select-none"
                                                            onClick={() => handleSort('sales')}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <DollarSign size={16} className="me-2" />
                                                                Sales
                                                                {getSortIcon('sales')}
                                                            </div>
                                                        </th>
                                                        <th className="border-0 fw-bold">
                                                            <Shield size={16} className="me-2" />
                                                            Refundable Deposit
                                                        </th>
                                                        <th className="border-0 fw-bold">
                                                            <Shield size={16} className="me-2" />
                                                            Security Deposit
                                                        </th>
                                                        <th className="border-0 fw-bold">
                                                            <Receipt size={16} className="me-2" />
                                                            GST
                                                        </th>
                                                        <th className="border-0 fw-bold text-center">
                                                            <Download size={16} className="me-2" />
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedData.map((row, index) => (
                                                        <tr key={index} className="align-middle">
                                                            <td className="fw-bold text-primary">
                                                                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                                                                    {row.month}
                                                                </span>
                                                            </td>
                                                            <td className="text-muted">
                                                                {formatDate(row.date)}
                                                            </td>
                                                            <td className="fw-bold text-success">
                                                                {formatCurrency(row.sales)}
                                                            </td>
                                                            <td className="text-info">
                                                                {formatCurrency(row.refundableDeposit)}
                                                            </td>
                                                            <td className="text-warning">
                                                                {formatCurrency(row.securityDeposit)}
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex justify-content-between">
                                                                        <span className="text-muted">CGST:</span>
                                                                        <span className="fw-bold">{formatCurrency(row.gst.cgst)}</span>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                        <span className="text-muted">SGST:</span>
                                                                        <span className="fw-bold">{formatCurrency(row.gst.sgst)}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    className="btn btn-success btn-sm d-flex align-items-center mx-auto"
                                                                    onClick={e => downloadSalesPDF(e, row.download)}
                                                                    disabled={downloadingId === row.download}
                                                                >
                                                                    {downloadingId === row.download ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                                            Downloading...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FileText size={14} className="me-2" />
                                                                            Download PDF
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        {pages > 1 && (
                                            <div className="card-footer bg-light">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="text-muted small">
                                                        Showing page {activePage} of {pages}
                                                    </div>
                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0">
                                                            <li className={`page-item ${activePage === 1 ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(1)}
                                                                    disabled={activePage === 1}
                                                                >
                                                                    <ChevronsLeft size={14} />
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${activePage === 1 ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(activePage - 1)}
                                                                    disabled={activePage === 1}
                                                                >
                                                                    <ChevronLeft size={14} />
                                                                </button>
                                                            </li>
                                                            
                                                            {/* Page Numbers */}
                                                            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                                                                const startPage = Math.max(1, activePage - 2);
                                                                const pageNum = startPage + i;
                                                                if (pageNum <= pages) {
                                                                    return (
                                                                        <li key={pageNum} className={`page-item ${activePage === pageNum ? 'active' : ''}`}>
                                                                            <button 
                                                                                className="page-link"
                                                                                onClick={() => onPageChange(pageNum)}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                            
                                                            <li className={`page-item ${activePage === pages ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(activePage + 1)}
                                                                    disabled={activePage === pages}
                                                                >
                                                                    <ChevronRight size={14} />
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${activePage === pages ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link"
                                                                    onClick={() => onPageChange(pages)}
                                                                    disabled={activePage === pages}
                                                                >
                                                                    <ChevronsRight size={14} />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <TrendingUp size={64} className="text-muted mb-3" />
                                        <h5 className="text-muted">No Sales Data Found</h5>
                                        <p className="text-muted mb-0">
                                            Try adjusting your date range or check back later for sales records.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

Sales.propTypes = {
    getSales: PropTypes.func.isRequired,
    downloadSales: PropTypes.func.isRequired,
    sales: PropTypes.array,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    sales: state.sales.sales,
    downloadBlob: state.sales.downloadBlob,
    pages: state.sales.pages,
    activePage: state.sales.activePage,
    loading: state.sales.loading
});

export default connect(mapStateToProps, { getSales, downloadSales })(Sales);